import { useFlags } from '@atlaskit/flag';
import { ProcessTypeStatusesApiService } from 'core/api';
import { useDialog } from 'core/providers/DialogProvider';
import SharedButton from 'shared/components/buttons/button';
import PageHeader from 'shared/components/page-header/page-header';
import SharedTable from 'shared/components/table/table';
import { ISharedTableColumn, ISharedTableCustomCellTemplate } from 'shared/components/table/table-interface';
import ConfirmationDialog from 'modules/dialogs/confirmation-dialog';
import { useCollection } from 'core/hooks/useCollection';
import { deleteItem, deleteItems } from 'core/utilities/api-wrapper';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import Checkbox from '@atlaskit/checkbox';
import SharedFilters, { Filter, SelectedValue } from 'shared/components/filters/filters';
import { PageElement } from 'core/utilities/interface-helpers';
import { QueryConstraint, where } from 'firebase/firestore';
import { IButton } from 'shared/components/buttons/button-interface';
import { IProcessTypeStatusDao } from 'core/api/types/process-type-statuses.interface';
import AddEditProcessTypeStatusDialog from './add-edit-process-type-status-dialog';

const ProcessTypeStatusesTableColumns: ISharedTableColumn[] = [
  { label: '', key: 'select', templateId: 'select', width: 1, isCompact: true },
  { label: 'Name', key: 'name', templateId: 'text', width: 10 },
  { label: 'Status', key: 'active', templateId: 'boolean', width: 10 },
  { label: '', key: 'action', templateId: 'action', width: 10 },
];

const ProcessTypeStatusesList = () => {
  const dialog = useDialog();
  const flags = useFlags();

  const [filterQuery, setFilterQuery] = useState<SelectedValue[]>([]);
  const [constraints, setConstraints] = useState<QueryConstraint[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const { tableRows, loading, loadMore, hasMore, loadingMore } = useCollection<IProcessTypeStatusDao>({
    apiService: ProcessTypeStatusesApiService,
    constraints,
  });

  useEffect(() => {
    setSelectedRows([]);
  }, [tableRows]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const newConstraints = filterQuery
        .filter((filter) => filter.value !== '' && !filter.id.includes('.'))
        .map((filter) => {
          const value = filter.value === 'true' ? true : filter.value === 'false' ? false : filter.value;
          return where(filter.id, '==', value);
        });

      setConstraints(newConstraints);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterQuery]);

  const handleFilterChange = useCallback(
    (values: SelectedValue[]) => {
      const currentSerialized = JSON.stringify(filterQuery.sort((a, b) => a.id.localeCompare(b.id)));
      const newSerialized = JSON.stringify(values.sort((a, b) => a.id.localeCompare(b.id)));

      if (currentSerialized !== newSerialized) {
        setFilterQuery(values);
      }
    },
    [filterQuery]
  );

  const rowChecked = (event: ChangeEvent<HTMLInputElement>, uid: string) => {
    if (event.target.checked) {
      setSelectedRows((prevState) => [...prevState, uid]);
    } else {
      setSelectedRows((prevState) => prevState.filter((selectedUid) => selectedUid !== uid));
    }
  };

  const selectCellTemplate = (processTypeStatus: IProcessTypeStatusDao) => {
    return (
      <Checkbox
        isChecked={selectedRows.includes(processTypeStatus.uid)}
        onChange={(e) => rowChecked(e, processTypeStatus.uid)}
      />
    );
  };

  const actionCellTemplate = (processTypeStatus: IProcessTypeStatusDao) => {
    return (
      <div className='w-full flex justify-end space-x-2'>
        <SharedButton
          onClick={() => dialog?.openDialog(<AddEditProcessTypeStatusDialog processTypeStatus={processTypeStatus} />)}
          type='button'
          appearance='subtle'
          spacing='compact'
          label='Update'
        />

        <SharedButton
          onClick={() =>
            dialog?.openDialog(
              <ConfirmationDialog
                title='Delete process type status'
                textContent='Are you sure you want to delete this process type status? This action cannot be undone.'
                action={() => deleteItem(ProcessTypeStatusesApiService, processTypeStatus, flags, dialog)}
                buttonLabel='Delete Process Type Status'
              />
            )
          }
          type='button'
          appearance='subtle'
          spacing='compact'
          label='Delete'
        />
      </div>
    );
  };

  const customTemplates: ISharedTableCustomCellTemplate[] = [
    { template: selectCellTemplate, id: 'select' },
    {
      template: actionCellTemplate,
      id: 'action',
    },
    { template: selectCellTemplate, id: 'select' },
  ];

  const headerActions: PageElement[] = [
    ...(selectedRows.length
      ? [
          {
            key: 'deleteSettingsProcessTypeStatuses',
            element: (
              <SharedButton
                onClick={() =>
                  dialog?.openDialog(
                    <ConfirmationDialog
                      title='Delete process type statuses'
                      textContent='Are you sure you want to delete these process type statuses? This action cannot be undone.'
                      action={() => deleteItems(ProcessTypeStatusesApiService, selectedRows, flags, dialog)}
                      buttonLabel='Delete Process Type Statuses'
                    />
                  )
                }
                type='button'
                appearance='danger'
                label='Delete Process Type Statuses'
              />
            ),
          },
        ]
      : [
          {
            key: 'addSettingsProcessTypeStatus',
            element: (
              <SharedButton
                onClick={() => dialog?.openDialog(<AddEditProcessTypeStatusDialog />)}
                type='button'
                appearance='primary'
                label='Add Process Type Status'
              />
            ),
          },
        ]),
  ];

  const filters: Filter[] = [
    {
      id: 'active',
      label: 'Active',
      options: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
      ],
    },
  ];

  const loadMoreButton: IButton = {
    onClick: () => loadMore(),
    type: 'submit',
    appearance: 'subtle',
    label: 'Load more',
    isLoading: loadingMore,
  };

  return (
    <>
      <PageHeader title='Process Type Statuses' actions={headerActions} />
      <SharedFilters filters={filters} onFilterValueChange={handleFilterChange} />
      <SharedTable
        columns={ProcessTypeStatusesTableColumns}
        customTemplates={customTemplates}
        rows={tableRows}
        isLoading={loading}
        loadMoreButton={hasMore ? loadMoreButton : undefined}
      />
    </>
  );
};

export default ProcessTypeStatusesList;
